// Types and Data for HelpSlovakiaSurvey survey

import { v4 as uuidv4 } from 'uuid'
import type { RouteLocationRaw as RawLocation } from 'vue-router'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'
import { SurveyQuestionTypes } from './SelfKnowledgeSurvey.ts'

export type HelpSlovakiaSurveyId = string
export interface HelpSlovakiaSurvey {
    id: HelpSlovakiaSurveyId
    surveyType: SurveyTypes
    userId: UserId
    hash?: string
    questions: HelpSlovakiaSurveyQuestionTypes[]
    score?: number
    createdAt: Timestamp
}
export interface HelpSlovakiaSurveyQuestionTypes {
    id: string
    type: SurveyQuestionTypes
    reversed?: boolean
    title: string
    value?: number
}

// Interpretation of result score
export interface HelpSlovakiaInterpretationType {
    title: string
    description: string
    buttons: {
        title: string
        to: RawLocation
    }[]
}

// All interpretations of result score
export interface HelpSlovakiaInterpretationsTypes {
    [scoreLevel.LOW]: HelpSlovakiaInterpretationType
    [scoreLevel.MEDIUM]: HelpSlovakiaInterpretationType
    [scoreLevel.HIGH]: HelpSlovakiaInterpretationType
}

export const HelpSlovakiaSurveyQuestions: HelpSlovakiaSurveyQuestionTypes[] = [
    {
        id: '1',
        type: SurveyQuestionTypes.SCALE,
        title: 'Ak niekto pracuje pre rozvoj svojho mesta či štátu, zaslúži si uznanie.'
    },
    {
        id: '2',
        type: SurveyQuestionTypes.SCALE,
        title: 'Osobne poznám ľudí, ktorí pracujú na rozvoji Slovenska.'
    },
    {
        id: '3',
        type: SurveyQuestionTypes.SCALE,
        title: 'Pokiaľ som vedel/a niekomu pomôcť, spravil/a som to bez nároku na to, aby mi to vrátil/a.'
    },
    {
        id: '4',
        type: SurveyQuestionTypes.SCALE,
        title: 'Ak by som sa pusti/al do projektu na podporu svojho regiónu, viem koho by som oslovil/a na spoluprácu.'
    },
    {
        id: '5',
        type: SurveyQuestionTypes.SCALE,
        title: 'Pravidelne prispievam na charitu alebo iné projekty pomoci.'
    },
    {
        id: '6',
        reversed: true,
        type: SurveyQuestionTypes.SCALE,
        title: 'Zmena v spoločnosti je v rukách politikov, bežný človek s tým nič nespraví.'
    },
    {
        id: '7',
        type: SurveyQuestionTypes.SCALE,
        title: 'Moji známi sa zapájajú do dobrovoľníckych alebo pro-bono aktivít.'
    },
    {
        id: '8',
        type: SurveyQuestionTypes.SCALE,
        title: 'Za posledné dva mesiace som pomohol/la niekomu mimo svojej rodiny a priateľov.'
    },
    {
        id: '9',
        reversed: true,
        type: SurveyQuestionTypes.SCALE,
        title: 'Slovensko ide tak zlým smerom, že jediným riešením je odchod do cudziny.'
    },
    {
        id: '10',
        type: SurveyQuestionTypes.SCALE,
        title: 'Verím, že aj malé lokálne aktivity môžu zlepšiť kvalitu života na Slovensku.'
    },
    {
        id: '11',
        reversed: true,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď vidím problém vo svojom okolí, skôr čakám, že ho vyrieši niekto kompetentný.'
    },
    {
        id: '12',
        type: SurveyQuestionTypes.SCALE,
        title: 'Mám predstavu, akej téme by som sa chcel/a venovať, keby som sa rozhodol/la pomáhať.'
    },
    {
        id: '13',
        type: SurveyQuestionTypes.SCALE,
        title: 'Viem si nájsť dôveryhodné iniciatívy alebo organizácie, cez ktoré sa dá pomáhať.'
    },
    {
        id: '14',
        reversed: true,
        type: SurveyQuestionTypes.SCALE,
        title: 'Verejné veci sú príliš zamotané na to, aby sa do nich oplatilo zapájať.'
    },
    {
        id: '15',
        type: SurveyQuestionTypes.SCALE,
        title: 'Som ochotný/á venovať pravidelne čas aktivite, ktorá má spoločenský prínos.'
    },
    {
        id: '16',
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď ma niečo na Slovensku trápi, viem sa o tom rozprávať s ľuďmi cez možné riešenia.'
    },
    {
        id: '17',
        reversed: true,
        type: SurveyQuestionTypes.SCALE,
        title: 'Nemám zručnosti ani skúsenosti, ktoré by mohli byť užitočné pre verejnoprospešné aktivity.'
    },
    {
        id: '18',
        type: SurveyQuestionTypes.SCALE,
        title: 'Podporujem ľudí, ktorí sa snažia zlepšovať veci okolo seba, aj keď s nimi nesúhlasím vo všetkom.'
    },
    {
        id: '19',
        reversed: true,
        type: SurveyQuestionTypes.SCALE,
        title: 'Ak by som sa zapojil/a do pomoci Slovensku, aj tak by to pravdepodobne nič nezmenilo.'
    },
    {
        id: '20',
        type: SurveyQuestionTypes.SCALE,
        title: 'V poslednom roku som si aspoň raz aktívne hľadal/a spôsob, ako sa zapojiť do pomoci iným alebo komunite.'
    }
]

export function createInitializedHelpSlovakiaSurvey(userId: UserId): Omit<HelpSlovakiaSurvey, 'id' | 'createdAt'> {
    return {
        userId,
        questions: HelpSlovakiaSurveyQuestions.map((question) => ({ ...question, value: undefined })),
        surveyType: SurveyTypes.HELP_SLOVAKIA,
        hash: uuidv4()
    }
}

export const initializedHelpSlovakiaSurvey = {
    questions: HelpSlovakiaSurveyQuestions.map((question) => ({ ...question, value: undefined })),
    surveyType: SurveyTypes.HELP_SLOVAKIA,
    hash: uuidv4()
} as HelpSlovakiaSurvey

// Until the end of 2024 the questionnaire was answered on a 1–6 scale (last such record is from
// 2024-11-20), since 2026 on 1–5. Scoring an old record as 1–5 gives results above 100 %.
const HELP_SLOVAKIA_SIX_POINT_SCALE_UNTIL = new Date('2025-01-01T00:00:00Z')

function toSurveyDate(value: unknown): Date | null {
    if (!value) return null
    if (value instanceof Date) return value
    const timestamp = value as { toDate?: () => Date; seconds?: number; _seconds?: number }
    if (typeof timestamp.toDate === 'function') return timestamp.toDate()
    const seconds = timestamp.seconds ?? timestamp._seconds
    if (typeof seconds === 'number') return new Date(seconds * 1000)
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }
    return null
}

/** Highest answer on the scale the survey was filled in on. Unknown date = today's 1–5 scale. */
export function getHelpSlovakiaScaleMax(createdAt: unknown): number {
    const date = toSurveyDate(createdAt)
    return date && date < HELP_SLOVAKIA_SIX_POINT_SCALE_UNTIL ? 6 : 5
}

/** One answer as points from 0 (least willing to help) to `scaleMax - 1`. */
export function getHelpSlovakiaQuestionScore(question: HelpSlovakiaSurveyQuestionTypes, scaleMax: number = 5): number {
    if (typeof question.value !== 'number') return 0

    return question.reversed ? scaleMax - question.value : question.value - 1
}

/** Share of the highest possible score, 0–100, over the answered questions. */
export function getHelpSlovakiaPercentage(questions: HelpSlovakiaSurveyQuestionTypes[], createdAt: unknown): number {
    const scaleMax = getHelpSlovakiaScaleMax(createdAt)
    const answered = questions.filter((question) => typeof question.value === 'number')
    if (!answered.length) return 0

    const total = answered.reduce((sum, question) => sum + getHelpSlovakiaQuestionScore(question, scaleMax), 0)
    return Math.round((total / (answered.length * (scaleMax - 1))) * 100)
}

export enum scoreLevel {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high'
}

export const helpSlovakiaInterpretationsData: HelpSlovakiaInterpretationsTypes = {
    [scoreLevel.LOW]: {
        title: 'Potrebuješ sa ešte posmeliť',
        description:
            'Momentálne sa zameriavaš na iné priority a na pomoc Slovensku už ti zostáva menej času či energie. Možno si to už s touto krajinou tak trochu aj vzdal/a a nevidíš v tom zmysel. Možno ťa tvoje okolie neinšpiruje alebo ťa od tvojich snáh priam odrádza. A možno len nevieš ako na to a tvoj čas ešte neprišiel. Chceme ťa preto podporiť a inšpirovať k pomoci projektom a témam, ktoré pre teba zmysel majú. Ktovie, možno ťa to nakopne a možno aj malý krok pre teba vie byť veľkým pre Slovensko.',
        buttons: [
            {
                title: 'Chcem pomáhať',
                to: { name: 'dobrovolnictvo' }
            },
            {
                title: 'Chcem si nájsť prácu',
                to: { name: 'praca' }
            }
        ]
    },
    [scoreLevel.MEDIUM]: {
        title: 'Chceš, len nevieš ako na to',
        description:
            'Slovensko je pre teba dôležité a ľudia, ktorí ho robia lepším majú tvoj rešpekt. Ešte ale nemáš jasno v tom, ako pridať ruku k dielu. Vo svojom okolí  nevidíš úspešné príklady ľudí ako ty, ktorí by dokázali pohnúť vecami alebo necítiš podporu k takému konaniu.. Prípadne už máš v hlave plán,  no ešte nie si ready na konkrétne kroky. Nech už je to akokoľvek, sme radi, že si na GROWNi a dovoľ nám ukázať ti, že aj tvoja troška môže pomôcť.',
        buttons: [
            {
                title: 'Chcem začať pomáhať',
                to: { name: 'verejny-sektor' }
            }
        ]
    },
    [scoreLevel.HIGH]: {
        title: 'Teba už nič nezastaví',
        description:
            'Wow, Slovensko máš ozaj v srdci! A to, že to myslíš naozaj vážne už dokazuješ aj skutkami. Tvoja chuť Slovensku pomáhať a rozvíjať ho je potvrdená ako tvojím konaním, tak aj ľuďmi, ktorými sa obklopuješ a celkovým mindsetom, ktorý máš. Vieš totiž, že len frflať, ale nevyhrnúť rukávy nikam nevedie. Veríme, že aj vďaka tebe sa tu budeme mať stále lepšie a lepšie.',
        buttons: [
            {
                title: 'Chcem zlepšovať Slovensko',
                to: { name: 'verejny-sektor' }
            }
        ]
    }
}
